import React, { useMemo, useState, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCentroid, toCanonicalCountryName, toDisplayCountryName } from '@/utils/countryCentroids';
import { marketShareVendorLabel, readVendorState, toCanonicalVendorNameFromDeal } from '@/lib/procurementCompetitors';
import { getDealDisplayValueUSD, getDealValueUSDForTotals } from '@/utils/procurementValueUsd';
import { filterVisibleDeals } from '@/lib/procurementVisibility';
import { useTranslation } from '@/components/utils/translations';

const GEO_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

function vendorPieFromDeals(deals, currentTenant, options) {
  const counts = {};
  for (const d of filterVisibleDeals(deals)) {
    const canon = marketShareVendorLabel(d, currentTenant, options);
    counts[canon] = (counts[canon] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([name, value]) => ({ name: String(name).trim() || 'Unknown', value }))
    .sort((a, b) => b.value - a.value);
}

function mapCountryLabel(name) {
  return toDisplayCountryName(name) || toCanonicalCountryName(name) || String(name || '').trim() || null;
}

function matchCountry(dealCountry, geoName) {
  if (!dealCountry || !geoName) return false;
  const labelDeal = mapCountryLabel(dealCountry);
  const labelGeo = mapCountryLabel(geoName);
  if (!labelDeal || !labelGeo) return false;
  return labelDeal.toLowerCase() === labelGeo.toLowerCase();
}

const PIE_COLORS = [
  '#0891b2', '#d97706', '#2563eb', '#7c3aed', '#db2777', '#059669', '#4f46e5', '#ea580c',
];

export default function CompetitorIntelligenceMap({
  deals,
  dealsForPie = [],
  selectedCountry,
  onCountrySelect,
  onClearSelection,
  currentTenant,
  summary,
  showTopVendors = false,
  onOpenDetail,
  /** Path A: pre-aggregated deal counts per country (skip scanning deals). */
  countryDealCounts = null,
  /** Path A: per-country USD total from /aggregate (for tooltips; not client-summed). */
  countryValueUSD = null,
  /** Path A: full-filter vendor market share when no map country is selected. */
  globalVendorPieData = null,
  collapseLmsFamily = false,
}) {
  const { t } = useTranslation();
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.8);
  const [center, setCenter] = useState([0, 20]);
  const [lastClickedGeoName, setLastClickedGeoName] = useState(null);

  useEffect(() => {
    if (selectedCountry) {
      const canonicalSelected = toCanonicalCountryName(selectedCountry);
      const geoName = lastClickedGeoName || canonicalSelected || selectedCountry;
      const c = getCentroid(geoName);
      if (c) {
        setCenter(c);
        setZoom(4);
      }
    } else {
      setLastClickedGeoName(null);
      setCenter([0, 20]);
      setZoom(1.8);
    }
  }, [selectedCountry, lastClickedGeoName]);

  const { byCountry, pieDataForCountry, byCountryDetail } = useMemo(() => {
    const byCountry = countryDealCounts ? { ...countryDealCounts } : {};
    const byCountryVendor = {};
    const byCountryDetail = {};

    if (countryDealCounts) {
      const byCountryDetailFromServer = {};
      const allCountryKeys = new Set([
        ...Object.keys(byCountry),
        ...Object.keys(countryValueUSD || {}),
      ]);
      allCountryKeys.forEach((k) => {
        const v = countryValueUSD?.[k];
        byCountryDetailFromServer[k] = {
          totalValueUSD: v != null && !Number.isNaN(Number(v)) ? Number(v) : 0,
          vendorToValue: {},
        };
      });

      if (dealsForPie?.length && dealsForPie.some((d) => readVendorState(d))) {
        return {
          byCountry,
          pieDataForCountry: vendorPieFromDeals(dealsForPie, currentTenant, { collapseLmsFamily }),
          byCountryDetail: byCountryDetailFromServer,
        };
      }
      if (globalVendorPieData?.length) {
        const pieData = globalVendorPieData
          .map(({ name, value }) => ({ name: String(name).trim() || 'Unknown', value }))
          .sort((a, b) => b.value - a.value);
        return { byCountry, pieDataForCountry: pieData, byCountryDetail: byCountryDetailFromServer };
      }
      const pieDeals = selectedCountry
        ? (dealsForPie.length > 0 ? dealsForPie : [])
        : deals;
      const pieVendorCounts = {};
      pieDeals.forEach((d) => {
        const canon = marketShareVendorLabel(d, currentTenant, { collapseLmsFamily });
        pieVendorCounts[canon] = (pieVendorCounts[canon] || 0) + 1;
      });
      const pieData = Object.entries(pieVendorCounts)
        .map(([name, value]) => ({ name: String(name).trim() || 'Unknown', value }))
        .sort((a, b) => b.value - a.value);
      return { byCountry, pieDataForCountry: pieData, byCountryDetail: byCountryDetailFromServer };
    }

    filterVisibleDeals(deals).forEach((d) => {
      const canonicalCountry = mapCountryLabel(d.country) || 'Unknown';
      const vendor = toCanonicalVendorNameFromDeal(d, currentTenant) || 'Unknown';
      const valueUSD = getDealValueUSDForTotals(d);

      if (!byCountry[canonicalCountry]) {
        byCountry[canonicalCountry] = 0;
        byCountryVendor[canonicalCountry] = {};
        byCountryDetail[canonicalCountry] = { totalValueUSD: 0, vendorToValue: {} };
      }
      byCountry[canonicalCountry] += 1;
      byCountryVendor[canonicalCountry][vendor] = (byCountryVendor[canonicalCountry][vendor] || 0) + 1;
      byCountryDetail[canonicalCountry].totalValueUSD += valueUSD;
      byCountryDetail[canonicalCountry].vendorToValue[vendor] =
        (byCountryDetail[canonicalCountry].vendorToValue[vendor] || 0) + valueUSD;
    });

    const pieDeals = filterVisibleDeals(selectedCountry
      ? (dealsForPie.length > 0 ? dealsForPie : [])
      : (dealsForPie.length > 0 ? dealsForPie : deals));
    const pieDataFromDeals = vendorPieFromDeals(pieDeals, currentTenant, { collapseLmsFamily });

    let pieData = pieDataFromDeals;
    if (pieData.length === 0 && selectedCountry) {
      const canonicalSelected = toCanonicalCountryName(selectedCountry);
      const countryKey =
        Object.keys(byCountryVendor).find((k) => matchCountry(k, canonicalSelected || selectedCountry)) ||
        canonicalSelected ||
        selectedCountry;
      const vendorCounts = byCountryVendor[countryKey] || {};
      if (Object.keys(vendorCounts).length > 0) {
        pieData = Object.entries(vendorCounts)
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value);
      }
    }

    return { byCountry, pieDataForCountry: pieData, byCountryDetail };
  }, [deals, dealsForPie, selectedCountry, currentTenant, countryDealCounts, globalVendorPieData, countryValueUSD, collapseLmsFamily]);

  const top3DealsByValue = useMemo(() => {
    if (!selectedCountry || !dealsForPie.length) return [];
    return [...filterVisibleDeals(dealsForPie)]
      .map((d) => ({ deal: d, valueUSD: getDealDisplayValueUSD(d) }))
      .filter(({ valueUSD }) => valueUSD > 0)
      .sort((a, b) => b.valueUSD - a.valueUSD)
      .slice(0, 3);
  }, [selectedCountry, dealsForPie]);

  const maxCount = useMemo(
    () => Math.max(...Object.values(byCountry).map((v) => (typeof v === 'number' ? v : 0)), 1),
    [byCountry]
  );

  const getFill = (geoName) => {
    const labelGeo = mapCountryLabel(geoName);
    const count =
      labelGeo && Object.prototype.hasOwnProperty.call(byCountry, labelGeo)
        ? byCountry[labelGeo]
        : 0;
    if (!count) return '#f1f5f9';
    const ratio = Math.min(count / maxCount, 1);
    const opacity = 0.3 + ratio * 0.6;
    return `#00C2C2${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  };

  const handleMouseEnter = (geo, evt) => {
    const name = geo.properties?.name || geo.properties?.NAME || geo.id || 'Unknown';
    const label = mapCountryLabel(name) || Object.keys(byCountry).find((k) => k.toLowerCase() === (name || '').toLowerCase());
    const count = label && Object.prototype.hasOwnProperty.call(byCountry, label) ? byCountry[label] : 0;
    const detail = label && byCountryDetail ? byCountryDetail[label] : null;
    const totalValueUSD = detail?.totalValueUSD ?? 0;
    const top3 = detail?.vendorToValue
      ? Object.entries(detail.vendorToValue)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
      : [];
    let content = '';
    if (count === 0) {
      content = `${name}\n${t('procMapNoClosedDeals')}`;
    } else {
      content =
        `${name}\n` +
        `${t('deals')}: ${count}\n` +
        `${t('procMapTotalValueUsd')}: ${totalValueUSD > 0 ? '$' + totalValueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}` +
        (top3.length ? `\n${t('procMapTop')}: ${top3.map(([v, val]) => `${v} ($${val.toLocaleString(undefined, { maximumFractionDigits: 0 })})`).join(', ')}` : '');
    }
    setTooltipContent(content);
    setTooltipPosition({ x: evt.clientX, y: evt.clientY });
  };

  const handleMouseLeave = () => setTooltipContent('');
  const handleClick = (geo) => {
    const name = geo.properties?.name || geo.properties?.NAME || geo.id;
    if (!name) return;
    setLastClickedGeoName(name);
    const label =
      mapCountryLabel(name) ||
      Object.keys(byCountry).find((k) => matchCountry(k, name)) ||
      name;
    onCountrySelect?.(label);
  };
  const isSelected = (geoName) =>
    selectedCountry && matchCountry(selectedCountry, geoName);

  return (
    <div className="flex flex-col lg:flex-row gap-4 w-full">
      <div className="relative flex-1 rounded-lg border border-gray-200 bg-white overflow-hidden min-h-[320px]">
        <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-2">
          {summary && (
            <div className="bg-white/95 backdrop-blur rounded-lg border-2 border-gray-300 px-5 py-3 shadow-md min-w-[140px]">
              <div className="text-sm text-gray-600 mb-1">{t('procMapCountries')}</div>
              <div className="text-lg font-bold text-[#0A1A2F]">
                {summary.totalCountries.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mt-2 mb-1">{t('deals')}</div>
              <div className="text-lg font-bold text-[#0A1A2F]">
                {summary.totalDeals.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mt-2 mb-1">{t('procMapTotalValue')}</div>
              <div className="text-lg font-bold text-[#0A1A2F]">
                {summary.totalValueUSD > 0 ? '$' + summary.totalValueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
              </div>
              {showTopVendors && summary.top3Vendors?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">
                    {summary.topVendorsByDealCount ? t('procTopVendorsByDeals') : t('procTopVendorsByValue')}
                  </div>
                  {summary.top3Vendors.map(({ name, valueUSD, dealCount }, i) => (
                    <div key={name} className="text-xs text-[#0A1A2F] truncate" title={name}>
                      {i + 1}. {name}
                      {summary.topVendorsByDealCount
                        ? `: ${(dealCount ?? 0).toLocaleString()} ${t('procRtbDealsLower')}`
                        : `: $${valueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {selectedCountry && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 gap-1"
              onClick={() => { setLastClickedGeoName(null); onClearSelection?.(); }}
            >
              <X className="w-3 h-3" />
              {t('procClear')}
            </Button>
          )}
        </div>
        <div className="absolute bottom-2 right-2 z-10 flex flex-col gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom((z) => Math.min(z + 0.5, 8))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom((z) => Math.max(z - 0.5, 0.5))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
        </div>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 120 }}
          className="w-full"
          style={{ minHeight: 320, height: 'min(40vh, 420px)' }}
        >
          <ZoomableGroup
            center={center}
            zoom={zoom}
            onMoveEnd={({ coordinates, zoom: z }) => {
              setCenter(coordinates);
              setZoom(z);
            }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties?.name || geo.properties?.NAME || geo.id;
                  const sel = isSelected(name);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getFill(name)}
                      stroke={sel ? '#0A1A2F' : '#e2e8f0'}
                      strokeWidth={sel ? 1.5 : 0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none', cursor: 'pointer' },
                        pressed: { outline: 'none' },
                      }}
                      onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleClick(geo)}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        {tooltipContent && (
          <div
            className="fixed z-[9999] px-3 py-2 text-xs bg-[#0A1A2F] text-white rounded shadow-lg pointer-events-none whitespace-pre-line max-w-[220px]"
            style={{ left: tooltipPosition.x + 10, top: tooltipPosition.y + 10 }}
          >
            {tooltipContent}
          </div>
        )}
      </div>

      <div className="w-full lg:w-80 flex-shrink-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-4">
        {selectedCountry && top3DealsByValue.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('procTop3DealsByValue')}</h4>
            <div className="space-y-2">
              {top3DealsByValue.map(({ deal, valueUSD }, i) => (
                <div
                  key={deal.deal_id ?? deal.id ?? i}
                  className={`flex items-center justify-between gap-2 text-sm py-1.5 px-2 rounded ${onOpenDetail ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={onOpenDetail ? () => onOpenDetail(deal) : undefined}
                  role={onOpenDetail ? 'button' : undefined}
                >
                  <span className="flex-1 min-w-0 truncate font-medium text-gray-900" title={deal.account_name || deal.institution_name || deal.title}>
                    {deal.account_name || deal.institution_name || deal.title || '—'}
                  </span>
                  <span className="shrink-0 font-semibold text-[#00C2C2]">
                    ${valueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          {selectedCountry ? `${lastClickedGeoName || mapCountryLabel(selectedCountry) || selectedCountry} — ${t('procMarketShare')}` : t('procMarketShare')}
        </h4>
        {pieDataForCountry.length > 0 ? (
            <>
              <div className="h-48 procurement-pie-chart" style={{ opacity: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie
                      data={pieDataForCountry}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                      nameKey="name"
                      isAnimationActive={false}
                      stroke="#e5e7eb"
                      strokeWidth={1}
                    >
                      {pieDataForCountry.map((entry, i) => (
                        <Cell key={`cell-${entry.name}-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={1} stroke="#e5e7eb" strokeWidth={1} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const item = payload[0];
                        const total = pieDataForCountry.reduce((a, d) => a + d.value, 0);
                        const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
                        return (
                          <div
                            className="px-3 py-2 text-xs rounded-md shadow-lg border-none"
                            style={{
                              backgroundColor: '#0A1A2F',
                              color: '#fff',
                            }}
                          >
                            <div className="font-semibold mb-1">{item.name}</div>
                            <div>{t('procPieAccountsPct', { n: item.value, pct })}</div>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto" role="list" aria-label={t('procMarketShareByVendor')}>
                {pieDataForCountry.map((row, i) => {
                  const total = pieDataForCountry.reduce((a, d) => a + d.value, 0);
                  const pct = total > 0 ? ((row.value / total) * 100).toFixed(0) : '0';
                  return (
                    <div key={row.name} className="flex items-center justify-between text-sm gap-2" role="listitem">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      <span className="flex-1 min-w-0 truncate font-medium" title={row.name}>{row.name}</span>
                      <span className="text-gray-600 shrink-0">{row.value}</span>
                      <span className="text-gray-500 text-xs shrink-0">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </>
        ) : (
          <p className="text-sm text-gray-500 py-4">
            {selectedCountry ? t('procNoIncumbentDataCountry') : t('procClickCountryForMarketShare')}
          </p>
        )}
        </div>
      </div>
    </div>
  );
}
